// Utils and Constants
import { RoadStretch } from "../../../constants/action-constants/RoadStretchActionConstants";
import {
  updateRoadTrace,
  updateImpDetails,
  getFilteredCityStretches,
  getFilteredStretchesForTouchPoints,
  constructAppliedFiltersDetailsMap,
  getFilteredStretchesForFOL,
} from "../../utils/RoadUtil";

const initialState = {
  loading: false,
  error: "",
  roadStretchDetailsMap: {},
  requestSuccess: false,
  originalCityStretchesCount: 0,
  originalCityStretchesMap: {},

  // Base Impressions for progress bars (We use the highest impressions as the base)
  baseOts: 0,

  // Below details stores the "roadStretch-selections"
  selectedStretches: {},

  // zone, location, routeType and mediaType filtered stretches map
  filteredStretchesMap: {},
  appliedFiltersDetailsMap: {
    zones: [],
    freqOptLocations: [],
    arterialRoutes: [],
    mediaTypes: [],

    isZonesApplied: false,
    isFreqOptLocationsApplied: false,
    isArterialRoutesApplied: false,
    isMediaTypesApplied: false,
    isAnyFilterApplied: false,

    filteredOptions: {},

    firstAppliedFilterOptions: {},
    secondAppliedFilterOptions: {},
    thirdAppliedFilterOptions: {},
    appliedFilterOrder: {},
    appliedFrom: "",
  },

  // Touch Points filtered stretches map from city stretches
  touchPointsFilteredStretchesMap: {},

  // Freq Opted Locations filtered stretches map from city stretches
  folFilteredStretchesMap: {},
};

function updateImpAndTraceDetails(roadStretches = []) {
  const updatedRoadStretchesMap = {};
  let baseOts = 0;

  roadStretches.forEach((stretch) => {
    const { roadStretch, ...otherFields } = stretch || {};

    // remove stretch if no media present
    const orgMedias = stretch?.orgMedias || [];
    if (orgMedias.length === 0) {
      return;
    }

    const updatedStretchWithTraceInfo = updateRoadTrace(roadStretch);
    const updatedStretch = { ...otherFields, ...updatedStretchWithTraceInfo };
    const updatedImpStretch = updateImpDetails(updatedStretch);
    updatedRoadStretchesMap[roadStretch.id] = updatedImpStretch;
    const { otsAvg, otsLitAvg } = updatedStretch;
    const impressions = otsLitAvg ?? otsAvg;
    baseOts = impressions > baseOts ? impressions : baseOts;
  });

  return { updatedRoadStretchesMap, baseOts };
}

export default (state = initialState, action) => {
  switch (action.type) {
    case RoadStretch.GET_CITY_ROAD_STRETCHES:
      return {
        ...state,
        loading: true,
      };

    case RoadStretch.GET_CITY_ROAD_STRETCHES_SUCCESS: {
      const { roadStretches = [], isStoreCityAllStretchesCount } =
        action.payload || {};

      const { updatedRoadStretchesMap, baseOts } =
        updateImpAndTraceDetails(roadStretches);

      // construct object to update on success
      const responseToUpdate = {
        roadStretchDetailsMap: updatedRoadStretchesMap,
        baseOts: baseOts,

        // updating this key as no filters applied from UI on initial loading of page
        filteredStretchesMap: updatedRoadStretchesMap,
      };

      if (isStoreCityAllStretchesCount) {
        responseToUpdate["originalCityStretchesCount"] = roadStretches.length;
        responseToUpdate["originalCityStretchesMap"] = updatedRoadStretchesMap;
      }

      return {
        ...state,
        ...responseToUpdate,
        requestSuccess: true,
        loading: false,
      };
    }

    case RoadStretch.GET_CITY_ROAD_STRETCHES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case RoadStretch.SELECT_ROAD_STRETCH: {
      const roadStretch = action.payload.roadStretch;
      return {
        ...state,
        selectedStretches: {
          ...state.selectedStretches,
          [roadStretch.id]: roadStretch,
        },
      };
    }

    case RoadStretch.SELECT_ROAD_STRETCHES: {
      const { roadStretchesMap } = action.payload;
      const newSelectedStretches = Object.keys(roadStretchesMap).reduce(
        (acc, eachStretchId) => {
          acc[eachStretchId] = roadStretchesMap[eachStretchId];
          return acc;
        },
        {}
      );
      return {
        ...state,
        selectedStretches: newSelectedStretches,
      };
    }

    case RoadStretch.UN_SELECT_ROAD_STRETCH: {
      const { roadStretch } = action.payload;
      const selectedStretches = { ...state.selectedStretches };
      delete selectedStretches[roadStretch.id];
      return {
        ...state,
        selectedStretches,
      };
    }

    case RoadStretch.SELECT_MULTI_ROAD_STRETCHES: {
      const { roadStretchesMap } = action.payload || {};
      return {
        ...state,
        selectedStretches: {
          ...state.selectedStretches,
          ...roadStretchesMap,
        },
      };
    }

    case RoadStretch.UN_SELECT_MULTI_ROAD_STRETCHES: {
      const { roadStretchesMap } = action.payload || {};
      const clonedSelectedStretches = { ...state.selectedStretches };

      // remove selection
      Object.keys(roadStretchesMap).forEach(
        (id) => delete clonedSelectedStretches[id]
      );

      return {
        ...state,
        selectedStretches: clonedSelectedStretches,
      };
    }

    case RoadStretch.GET_TOUCH_POINTS_FILTERED_STRETCHES: {
      const { touchPoints = [], setSelectedTab } = action.payload || {};
      const touchPointsFilteredStretches = getFilteredStretchesForTouchPoints(
        state.originalCityStretchesMap,
        touchPoints
      );

      // close the tab
      if (setSelectedTab) {
        setSelectedTab(0);
      }

      return {
        ...state,
        touchPointsFilteredStretchesMap: touchPointsFilteredStretches,
      };
    }

    case RoadStretch.FILTER_CITY_ROAD_STRETCHES: {
      const {
        zones,
        freqOptLocations,
        arterialRoutes,
        mediaTypes,
        appliedFrom,
        setSelectedTab,
        isRestorePlan = false,
      } = action.payload || {};

      const filteredStretchesMap = getFilteredCityStretches(
        state.originalCityStretchesMap,
        zones,
        arterialRoutes,
        mediaTypes
      );

      const appliedFiltersDetailsMap = constructAppliedFiltersDetailsMap(
        state.originalCityStretchesMap,
        zones,
        freqOptLocations,
        arterialRoutes,
        mediaTypes,
        appliedFrom,
        state.appliedFiltersDetailsMap
      );

      // close the tab
      if (setSelectedTab) {
        setSelectedTab(0);
      }

      const finalAppliedFiltersDetailsMap = !isRestorePlan
        ? appliedFiltersDetailsMap
        : {};

      return {
        ...state,
        filteredStretchesMap: filteredStretchesMap,
        appliedFiltersDetailsMap: finalAppliedFiltersDetailsMap,
      };
    }

    case RoadStretch.RESTORE_APPLIED_FILTERS_DETAILS_MAP: {
      const { appliedFiltersDetailsMap } = action.payload || {};
      return {
        ...state,
        appliedFiltersDetailsMap: appliedFiltersDetailsMap,
      };
    }

    case RoadStretch.GET_FOL_FILTERED_STRETCHES: {
      const { freqOptLocations } = action.payload || {};
      const folFilteredStretches = getFilteredStretchesForFOL(
        state.originalCityStretchesMap,
        freqOptLocations
      );

      return {
        ...state,
        folFilteredStretchesMap: folFilteredStretches,
      };
    }

    case RoadStretch.RESET_ROAD_STRETCHES:
      return initialState;

    default:
      return state;
  }
};
